<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'TlbsBaseMap',
  
})
</script>
<template>
  <div
    ref="mapContainerRef"
    class="tlbs-base-map"
  >
    <slot />
  </div>
</template>

<script setup lang="ts" name="TlbsBaseMap">
import { mapAttrsHandlers } from './map-attrs-handlers';
import useLayerManager from './useLayerManager/index';
import useTools from './tools/useTools';
import omit from 'lodash/omit';
import isUndefined from 'lodash/isUndefined';
import { WatchStopHandle, provide } from 'vue';

const reinitProps = ['showControl', 'mapZoomType', 'subkey'];
const uninitialProps = ['zoomControlPostion', 'rotationControlPostion'];

interface Props {
  latitude?: number;
  longitude?: number;
  zoom?: number;
  rotation?: number;
  pitch?: number;
  scale?: number;
  draggable?: boolean;
  viewMode?: '2D' | '3D';
  showControl?: boolean;
  zoomRange?: number[];
  offset?: OffsetOption;
  mapZoomType?: number;
  zoomControlPostion?: number;
  rotationControlPostion?: number;
  vectorBaseMap?: VectorBaseMapOption;
  satelliteBaseMap?: SatelliteBaseMapOption;
  subkey?: string;
  mapStyleId?: string;
  enableExtendZoom?: boolean;
  measureToolType?: string;
  enableRealtimeLatlng?: boolean;
  enableRoam?: boolean;
  roamDuration?: number;
  roamLoop?: number;
  roamData?: RoamKeyFrame[];
  layers?: MapLayer[];
  showSatellite?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  latitude: 39.908755,
  longitude: 116.397476,
  zoom: 8,
  rotation: 0,
  pitch: 0,
  scale: 1,
  draggable: true,
  viewMode: '3D',
  showControl: true,
  zoomRange: () => [3, 20],
  offset: () => ({ x: 0, y: 0 }),
  mapZoomType: TMap.constants.MAP_ZOOM_TYPE.DEFAULT,
  zoomControlPostion: TMap.constants.CONTROL_POSITION.TOP_RIGHT,
  rotationControlPostion: TMap.constants.CONTROL_POSITION.TOP_RIGHT,
  vectorBaseMap: () => ({
    base: true,
    building3d: true,
    building2d: false,
    point: true,
    label: true,
    arrow: true,
  }),
  satelliteBaseMap: () => ({
    base: true,
    road: true,
  }),
  subkey: '',
  mapStyleId: '',
  enableExtendZoom: false,
  measureToolType: '',
  enableRealtimeLatlng: false,
  enableRoam: false,
  roamDuration: 10,
  roamLoop: 1,
  roamData: () => [],
  layers: () => [],
  showSatellite: false,
});

const emit = defineEmits<{
  (e: 'tilesloaded' | 'zoom' | 'centerChanged' | 'boundsChanged' | 'scaleChanged' | 'animationEnded' | 'animationStopped'): void;
  (e: 'click' | 'rightclick', evt: MapEvent): void;
  (e: 'animationPlaying', evt: AnimationEvent): void;
}>();

const mapContainerRef = ref();

const mapInstance = shallowRef<LooseObject | null>(null);

provide('mapInstance', mapInstance);
let clearAllLayers: undefined | (() => void);
let mapToolAttrsHandlers: LooseObject;
let destroyTools: () => void;
const watcherList = shallowRef<WatchStopHandle[]>([]);

onMounted(() => {
  initMap();
});

const initMap = (): void => {
  const options = {
    center: new TMap.LatLng(props.latitude, props.longitude),
    zoom: props.zoom,
    rotation: props.rotation,
    pitch: props.pitch,
    scale: props.scale,
    draggable: props.draggable,
    viewMode: props.viewMode,
    showControl: props.showControl,
    minZoom: props.zoomRange[0],
    maxZoom: props.zoomRange[1],
    enableExtendZoom: props.enableExtendZoom,
    offset: props.offset,
    mapZoomType: props.mapZoomType,
    subkey: props.subkey,
    subpid: location.host,
    baseMap: {
      type: props.showSatellite ? 'satellite' : 'vector',
      features: Object.entries(props.showSatellite ? props.satelliteBaseMap : props.vectorBaseMap)
        .filter(([, value]) => value)
        .map(([feature]) => feature),
    },
    renderOptions: {
      preserveDrawingBuffer: true, // 为了地图截图
    },
  };
  // todo ts
  if (props.mapStyleId) options.mapStyleId = props.mapStyleId;
  mapInstance.value = new TMap.Map(mapContainerRef.value, options);
  ({ mapToolAttrsHandlers, destroyTools } = useTools(mapInstance.value, mapContainerRef.value, props));
  clearAllLayers = useLayerManager(mapInstance.value, toRef(props, 'layers'))?.clearAllLayers;
  handleUninitialProps();
  setMapEvnets(mapInstance.value);
  setPropsWatcher();
};


const setPropsWatcher = (): void => {
  Object.keys(omit(props, 'layers')).forEach((attr) => {
    watcherList.value.push(watch(() => props[attr], (newVal) => {
      if (reinitProps.includes(attr)) {
        destroyMap();
        initMap();
      } else {
        if (mapAttrsHandlers[attr]) {
          mapAttrsHandlers[attr](newVal, mapInstance.value, props);
        } else if (mapToolAttrsHandlers[attr]) {
          mapToolAttrsHandlers[attr](newVal);
        } else {
          console.warn('无法处理该属性变化：', attr);
        }
      }
    }));
  });
  // 针对图层做特殊监听
  watcherList.value.push(watch(() => props.layers, () => {
    // clearAllLayers?.();
    ({ clearAllLayers } = useLayerManager(mapInstance.value, toRef(props, 'layers')));
  }));
};

const setMapEvnets = (mapInstance: LooseObject | null): void => {
  mapInstance
    ?.on('tilesloaded', () => emit('tilesloaded'))
    .on('click', (evt: MapEvent) => emit('click', evt))
    .on('rightclick', (evt: MapEvent) => emit('rightclick', evt))
    .on('zoom', () => emit('zoom'))
    .on('center_changed', () => emit('centerChanged'))
    .on('bounds_changed', () => emit('boundsChanged'))
    .on('scale_changed', () => emit('scaleChanged'))
    .on('animation_playing', (evt: AnimationEvent) => emit('animationPlaying', evt))
    .on('animation_ended', () => emit('animationEnded'))
    .on('animation_stopped', () => emit('animationStopped'));
};

const handleUninitialProps = (): void => {
  uninitialProps.forEach((key: string) => {
    if (!isUndefined(props[key])) {
      mapAttrsHandlers[key](props[key], mapInstance.value);
    }
  });
};

onUnmounted(() => {
  destroyMap();
});

const destroyMap = (): void => {
  destroyTools();
  clearAllLayers?.();
  mapInstance.value?.destroy();
  mapInstance.value = null;
  watcherList.value.forEach(unwatch => unwatch());
  watcherList.value = [];
};

defineExpose({
  map: mapInstance,
});
</script>

